/**
 * Stall system — pure logic (no Phaser imports).
 * Phaser rendering and scene wiring lives in StageScene.
 */
import type { StallDef } from '@/data/stalls';
import type { WeaponDef } from '@/data/weapons';
import { WORLD_WEAPONS } from '@/data/weapons';

export interface StallState {
  readonly def: StallDef;
  health: number;
  destroyed: boolean;
}

export type StallDrop = { kind: 'weapon'; def: WeaponDef } | { kind: 'food'; heal: number } | null;

/** Create a fresh stall state from a definition. */
export function createStallState(def: StallDef): StallState {
  return { def, health: def.health, destroyed: false };
}

/**
 * Apply one hit to a stall.
 * Returns true the moment the stall transitions to destroyed; false otherwise.
 * No-ops (returns false) if already destroyed.
 */
export function damageStall(stall: StallState, amount = 1): boolean {
  if (stall.destroyed) return false;
  stall.health = Math.max(0, stall.health - amount);
  if (stall.health <= 0) {
    stall.destroyed = true;
    return true;
  }
  return false;
}

/**
 * Resolve what a stall drops on destruction.
 * Returns null if the weapon id is unrecognised or the drop kind is unknown.
 */
export function resolveStallDrop(def: StallDef): StallDrop {
  if (def.dropKind === 'weapon') {
    const weapon = WORLD_WEAPONS.find((w) => w.id === def.dropWeaponId);
    return weapon ? { kind: 'weapon', def: weapon } : null;
  }
  if (def.dropKind === 'food') {
    return { kind: 'food', heal: def.dropHeal ?? 0 };
  }
  return null;
}
